import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Loader2, Save, ArrowDown, Wand2 } from "lucide-react";

// Canonical bin code (mirrors backend _normalize_location): 'gc1' / 'GC-1' -> 'G-C1'.
const normBin = (s) => { const c = String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, ""); return c.length >= 2 ? c[0] + "-" + c.slice(1) : c; };

/**
 * Excel-style bulk location assignment for a PO.
 * Rows = PO lines (SKU / product / qty / location). Assign a bin by typing a code
 * (datalist suggests existing bins; new codes are created on save) — seeded from the
 * uploaded "Location" column. "Fill down" copies a row's bin to every row below, and
 * "Fill blanks" mass-fills empty rows. Saves as planned locations per line.
 */
export default function LocationAssignGrid({ poId, lines = [], planned = [], onSaved }) {
  const [locations, setLocations] = useState([]);
  const [assign, setAssign] = useState({});
  const [bulk, setBulk] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingLocs, setLoadingLocs] = useState(true);

  useEffect(() => {
    api.get("/inventory/locations")
      .then(({ data }) => setLocations(data.locations || []))
      .catch(() => setLocations([]))
      .finally(() => setLoadingLocs(false));
  }, []);

  // Seed from existing planned (first bin per line), then fall back to the uploaded location.
  useEffect(() => {
    const seed = {};
    planned.forEach((p) => { if (!seed[p.po_line_id]) seed[p.po_line_id] = p.location_code; });
    lines.forEach((l) => { if (!seed[l.id] && l.customer_location) seed[l.id] = normBin(l.customer_location); });
    setAssign(seed);
  }, [planned, lines]);

  const codes = useMemo(() => locations.map((l) => l.code), [locations]);
  const assignedCount = lines.filter((l) => assign[l.id]).length;

  const setRow = (lineId, code) => setAssign((a) => ({ ...a, [lineId]: code }));

  const fillDown = (fromIdx) => {
    const code = assign[lines[fromIdx].id];
    if (!code) { toast.error("Pick a bin on this row first, then fill down."); return; }
    setAssign((a) => {
      const next = { ...a };
      for (let i = fromIdx; i < lines.length; i++) next[lines[i].id] = code;
      return next;
    });
  };

  const applyBulk = () => {
    if (!bulk) { toast.error("Pick a bin to apply."); return; }
    setAssign((a) => {
      const next = { ...a };
      lines.forEach((l) => { if (!next[l.id]) next[l.id] = bulk; });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      for (const l of lines) {
        const code = normBin(assign[l.id]);
        if (!code) continue;
        const existing = planned.filter((p) => p.po_line_id === l.id);
        // Skip only when it's already exactly one row with the same bin AND the
        // correct (expected) quantity; otherwise replace so planned_qty is fixed.
        if (existing.length === 1 && existing[0].location_code === code && existing[0].planned_qty === (l.expected_qty || 0)) continue;
        for (const p of existing) await api.delete(`/inventory/planned/${p.id}`);
        await api.post(`/inventory/pos/${poId}/planned`, {
          po_line_id: l.id, location_code: code, planned_qty: l.expected_qty || 0,
        });
      }
      toast.success("Locations assigned");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="loc-assign-grid">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-violet-600" />
          <h4 className="text-sm font-bold text-slate-800">Assign bin locations</h4>
          <span className="text-xs text-slate-400">{assignedCount}/{lines.length} assigned</span>
        </div>
        <div className="flex items-center gap-2">
          <input list="loc-assign-codes" value={bulk} onChange={(e) => setBulk(e.target.value.toUpperCase())} data-testid="loc-assign-bulk-select" placeholder="Bin for all blanks…" className="px-2.5 py-2 border border-slate-300 rounded-md text-sm bg-white min-w-[9rem] font-mono uppercase" />
          <button onClick={applyBulk} data-testid="loc-assign-apply-all" className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold px-3 py-2 rounded-md"><Wand2 className="w-4 h-4" /> Fill blanks</button>
          <button onClick={save} disabled={saving || loadingLocs} data-testid="loc-assign-save" className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-md">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
        </div>
      </div>
      <datalist id="loc-assign-codes">{codes.map((c) => <option key={c} value={c} />)}</datalist>

      {locations.length === 0 && !loadingLocs && (
        <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-b border-slate-200">No bins created yet — just type a code (e.g. <b>G-C1</b>); it&apos;ll be created when you save.</div>
      )}

      <div className="overflow-x-auto max-h-[420px]">
        <table className="w-full text-left text-sm" data-testid="loc-assign-table">
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 px-3 font-medium w-10">#</th>
              <th className="py-2 px-3 font-medium">SKU</th>
              <th className="py-2 px-3 font-medium">Product</th>
              <th className="py-2 px-3 font-medium w-16">Qty</th>
              <th className="py-2 px-3 font-medium w-56">Location</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => (
              <tr key={l.id} className={`border-b border-slate-50 ${assign[l.id] ? "bg-emerald-50/30" : ""}`} data-testid={`loc-assign-row-${l.id}`}>
                <td className="py-1.5 px-3 text-slate-400 text-xs">{idx + 1}</td>
                <td className="py-1.5 px-3 font-mono text-xs">{l.sku}</td>
                <td className="py-1.5 px-3 text-slate-700 truncate max-w-[16rem]">{l.product_title || "—"}{l.variant_title ? ` · ${l.variant_title}` : ""}</td>
                <td className="py-1.5 px-3 font-semibold text-slate-800">{l.expected_qty}</td>
                <td className="py-1.5 px-3">
                  <div className="flex items-center gap-1">
                    <input
                      list="loc-assign-codes"
                      value={assign[l.id] || ""}
                      onChange={(e) => setRow(l.id, e.target.value.toUpperCase())}
                      data-testid={`loc-assign-select-${l.id}`}
                      placeholder="— none —"
                      className="flex-1 px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white font-mono uppercase"
                    />
                    <button onClick={() => fillDown(idx)} data-testid={`loc-assign-filldown-${l.id}`} title="Fill this bin down to all rows below" className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-slate-100"><ArrowDown className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
